import { createAsyncThunk } from "@reduxjs/toolkit";
import { backend, CaseClientStatus, ProcessingDetails, emptyLensHitFilters } from "Backend";
import {
  Case,
  CaseInfo,
  DocumentIntake,
  DocumentSegment,
  GetCaseByIdDocument,
  GetCaseDocumentQuery,
  GetCaseDocumentsDocument,
  GetCaseHomeDocument,
  GetCaseHomeQuery,
  GetPossibleChangesFromAutoScriptDocument,
  GetPossibleChangesFromAutoScriptQuery,
  KeyValueOptionalInput,
  NamedUser,
  OutcomeStatus,
  SectionValue,
  SetDocumentsReviewedDocument,
  UpdateSectionOutcomeDocument,
  UpdateSectionValidationDocument,
  UpdateStatusDocument,
  ValidationStatus,
  DeleteDocumentIntakeDocument,
  GetCaseDocumentDocument,
  GetCaseSegmentDocument,
  RenameDocumentIntakeDocument,
  ReprocessIntakeDocument,
  ReprocessType,
  UpdateIntakeCustomPropertiesDocument,
  CaseExport,
  GetCaseExportsDocument,
  GetCaseExportsQuery,
  UpdateCaseExportCollectSharedStateDocument,
  GetCaseFindingsQueryVariables,
  GetCaseFindingsQuery,
  GetCaseFindingsDocument,
  LensHitsFilterParamsInput,
  SetCollaboratorFirmsDocument,
  NamedFirm,
  GetPossibleChangesFromAutoScriptQueryVariables,
  SectionReviewState,
  UpdateSectionOutcomeCommentDocument,
  UpdateSectionValidationCommentDocument,
  UpdateSectionOutcomeAssigneeDocument,
  UpdateSectionValidationAssigneeDocument,
  UpdateSectionValuesReviewStateDocument,
  ClearSectionValidationAssigneeDocument,
  ClearSectionOutcomeAssigneeDocument,
  UpdateSectionOutcomeStatusDocument,
  UpdateSectionValidationStatusDocument,
  SectionValueView,
  GetCaseSectionValuesQueryVariables,
  GetCaseSectionValuesDocument,
  BulkCaseProfileExportDocument,
  BulkCaseProfileExportMutationVariables,
  SetPlaintiffFirmDocument,
  UpdateClientCaseIdDocument,
} from "generated/graphql";
import { runGqlMutation, runGqlQuery } from "gqlClient";
import type { DocumentSegmentStaged } from "./case.slice";
import type { RootState } from "redux/store";
import { extractGQLError } from "shared/PatternGraphQLError";

export interface CaseFetchProps {
  caseId: number;
}

export interface UpdateCaseClaimantInfoProps {
  caseId: number;
  caseInfo: CaseInfo;
  userAssignee: NamedUser | undefined;
  caseProperties: KeyValueOptionalInput[];
}

export interface UpdateCaseSummaryProps {
  caseId: number;
  summary: string;
}

export interface UpdateCaseClientStatusProps {
  caseId: number;
  status: CaseClientStatus;
}

export interface UpdateCaseReviewStatusProps {
  caseId: number;
  reviewed: boolean;
  reviewStatusReason?: string;
}

export const fetchCaseHome = createAsyncThunk<GetCaseHomeQuery, { caseId: number; lensId: number }>(
  "case/fetchCaseHome",
  (params) => runGqlQuery(GetCaseHomeDocument, params)
);

export const fetchPossibleChangesFromAutoScripts = createAsyncThunk<
  GetPossibleChangesFromAutoScriptQuery,
  GetPossibleChangesFromAutoScriptQueryVariables
>("case/runAutoScriptsEvent", (params) => runGqlQuery(GetPossibleChangesFromAutoScriptDocument, params));

export const fetchCase = createAsyncThunk<Case, CaseFetchProps>("case/fetchCase", ({ caseId }) =>
  runGqlQuery(GetCaseByIdDocument, { caseId }).then((r) => r.case.data)
);

export const fetchCaseSilent = createAsyncThunk<Case, CaseFetchProps>("case/fetchCaseSilent", ({ caseId }) =>
  runGqlQuery(GetCaseByIdDocument, { caseId }).then((r) => r.case.data)
);

export const updateCaseClaimantInfo = createAsyncThunk<
  { caseId: number; info: CaseInfo; userAssignee: NamedUser | undefined; caseProperties: KeyValueOptionalInput[] },
  UpdateCaseClaimantInfoProps
>("case/updateCaseClaimantInfo", async ({ caseId, caseInfo, userAssignee, caseProperties }) => {
  const info = await backend.editCaseClaimantInfo(caseId, caseInfo, userAssignee?.id, caseProperties);
  return { info, userAssignee, caseId, caseProperties };
});

export const updateCaseSummary = createAsyncThunk<string, UpdateCaseSummaryProps>(
  "case/updateCaseSummary",
  async ({ caseId, summary }) => {
    if (summary === "") await backend.setCaseSummary(caseId, { type: "Clear" });
    else await backend.setCaseSummary(caseId, { type: "Set", value: summary });
    return summary;
  }
);

export const updateCaseClientCaseId = createAsyncThunk<
  { caseId: number; clientCaseId: string },
  { caseId: number; clientCaseId: string }
>("case/updateCaseClientCaseId", (props, thunkAPI) =>
  runGqlMutation(UpdateClientCaseIdDocument, props)
    .then(() => props)
    .catch((e) => thunkAPI.rejectWithValue(extractGQLError(e)))
);

export const updateCaseClientStatus = createAsyncThunk<CaseClientStatus, UpdateCaseClientStatusProps>(
  "case/updateCaseClientStatus",
  (props) => runGqlMutation(UpdateStatusDocument, props).then(() => props.status)
);

export const setDocumentsReviewed = createAsyncThunk<
  { reviewed: boolean; reviewStatusReason?: string },
  UpdateCaseReviewStatusProps
>("case/updateCaseReviewStatus", (props) => {
  return runGqlMutation(SetDocumentsReviewedDocument, props).then(() => {
    return { reviewed: props.reviewed, reviewStatusReason: props.reviewStatusReason };
  });
});

export const updateSectionValueOutcome = createAsyncThunk<
  SectionValue,
  {
    outcomeStatus: OutcomeStatus;
    outcomeComment: string;
    caseId: number;
    formId: number;
    sectionId: number;
  }
>("case-profile/updateSectionOutcomeComment", (props) =>
  runGqlMutation(UpdateSectionOutcomeDocument, props).then((res) => res.case.form.putSectionOutcome)
);

export const updateSectionValueValidation = createAsyncThunk<
  SectionValue,
  {
    validationStatus: ValidationStatus;
    validationComment: string;
    caseId: number;
    formId: number;
    sectionId: number;
  }
>("case-profile/updateSectionValidationComment", (props) =>
  runGqlMutation(UpdateSectionValidationDocument, props).then((res) => res.case.form.putSectionValidation)
);

export const updateSectionValueOutcomeComment = createAsyncThunk<
  SectionValue[],
  {
    caseId: number;
    sectionId: number;
    comment: string;
    formId: number;
  }
>("case-profile/updateSectionValueOutcomeComment", (props) =>
  runGqlMutation(UpdateSectionOutcomeCommentDocument, props).then((res) => res.case.form.putSectionValues)
);

export const updateSectionValueValidationComment = createAsyncThunk<
  SectionValue[],
  {
    caseId: number;
    sectionId: number;
    comment: string;
    formId: number;
  }
>("case-profile/updateSectionValueValidationComment", (props) =>
  runGqlMutation(UpdateSectionValidationCommentDocument, props).then((res) => res.case.form.putSectionValues)
);

export const updateSectionValueOutcomeStatus = createAsyncThunk<
  SectionValue[],
  {
    caseId: number;
    sectionId: number;
    status: OutcomeStatus;
    formId: number;
  }
>("case-profile/updateSectionOutcomeStatus", (props) =>
  runGqlMutation(UpdateSectionOutcomeStatusDocument, props).then((res) => res.case.form.putSectionValues)
);

export const updateSectionValueValidationStatus = createAsyncThunk<
  SectionValue[],
  {
    caseId: number;
    sectionId: number;
    status: ValidationStatus;
    formId: number;
  }
>("case-profile/updateSectionValidationStatus", (props) =>
  runGqlMutation(UpdateSectionValidationStatusDocument, props).then((res) => res.case.form.putSectionValues)
);

export const updateSectionValueOutcomeAssignee = createAsyncThunk<
  {
    sectionValues: SectionValue[];
    caseId: number;
  },
  {
    caseId: number;
    sectionId: number;
    formId: number;
    assigneeId?: number;
  }
>("case-profile/updateSectionValueOutcomeAssignee", (props) => {
  const assigneeId = props.assigneeId;
  if (assigneeId !== undefined) {
    return runGqlMutation(UpdateSectionOutcomeAssigneeDocument, { ...props, assigneeId }).then((res) => ({
      caseId: props.caseId,
      sectionValues: res.case.form.putSectionValues,
    }));
  } else {
    return runGqlMutation(ClearSectionOutcomeAssigneeDocument, props).then((res) => ({
      caseId: props.caseId,
      sectionValues: res.case.form.putSectionValues,
    }));
  }
});

export const fetchCaseSectionValues = createAsyncThunk<SectionValueView[], GetCaseSectionValuesQueryVariables>(
  "case/doc-upload",
  (props, thunkAPI) => {
    return runGqlQuery(GetCaseSectionValuesDocument, props)
      .then((r) => r.case.data.sectionValues)
      .catch((e) => thunkAPI.rejectWithValue(extractGQLError(e)));
  }
);

export const updateSectionValueValidationAssignee = createAsyncThunk<
  {
    sectionValues: SectionValue[];
    caseId: number;
  },
  {
    caseId: number;
    sectionId: number;
    formId: number;
    assigneeId?: number;
  }
>("case-profile/updateSectionValueValidationAssignee", (props) => {
  const assigneeId = props.assigneeId;
  if (assigneeId !== undefined) {
    return runGqlMutation(UpdateSectionValidationAssigneeDocument, { ...props, assigneeId }).then((res) => ({
      caseId: props.caseId,
      sectionValues: res.case.form.putSectionValues,
    }));
  } else {
    return runGqlMutation(ClearSectionValidationAssigneeDocument, props).then((res) => ({
      caseId: props.caseId,
      sectionValues: res.case.form.putSectionValues,
    }));
  }
});

export const updateSectionValuesReviewState = createAsyncThunk<
  SectionValue[],
  {
    caseId: number;
    formId: number;
    inputs: {
      sectionId: number;
      setReviewState: SectionReviewState;
    }[];
  }
>("case-profile/updateSectionValueReviewState", (props) =>
  runGqlMutation(UpdateSectionValuesReviewStateDocument, props).then((res) => res.case.form.putSectionValues)
);

/** Case Intake Actions */
interface CaseIntakesFetchProps {
  caseId: number;
  forceRefresh: boolean;
}

export function segmentContainsPage(segmentStartPage: number, segmentPageCount: number, intakePage: number): boolean {
  return intakePage >= segmentStartPage && intakePage < segmentStartPage + segmentPageCount;
}

export function findDocumentSegmentInPageRange(
  intake: DocumentIntake,
  intakePageNumber: number
): DocumentSegment | undefined {
  return intake?.documentSegments.find((ds) => {
    return segmentContainsPage(ds.startPage, ds.pageCount, intakePageNumber);
  });
}

export const fetchCaseIntakes = createAsyncThunk<DocumentIntake[], CaseIntakesFetchProps, { state: RootState }>(
  "case/fetchAllIntakes",
  ({ caseId, forceRefresh = false }, thunkAPI) => {
    const state = thunkAPI.getState();
    const currentCaseId = state.case.case.id;

    if (!forceRefresh && currentCaseId === caseId && state.case.intakes.length) {
      return Promise.resolve(state.case.intakes);
    } else {
      return runGqlQuery(GetCaseDocumentsDocument, { caseId }).then((r) => r.case.documents);
    }
  }
);

export const fetchIntake = createAsyncThunk<GetCaseDocumentQuery, { caseId: number; intakeId: number }>(
  "case/fetchSingleIntake",
  ({ caseId, intakeId }) => runGqlQuery(GetCaseDocumentDocument, { caseId, intakeId })
);

export const fetchSegment = createAsyncThunk<
  DocumentSegmentStaged,
  { caseId: number; segmentId: number; processingDetails?: ProcessingDetails }
>("case/fetchIntakeSegment", ({ caseId, segmentId, processingDetails }) =>
  runGqlQuery(GetCaseSegmentDocument, { caseId, segmentId }).then((res) => ({
    ...res.case.segment,
    processingDetails,
  }))
);

export const deleteIntake = createAsyncThunk<{ intakeId: number }, { intakeId: number }>(
  "case/deleteIntake",
  ({ intakeId }) => runGqlMutation(DeleteDocumentIntakeDocument, { intakeId }).then(() => ({ intakeId }))
);

export const setCollaboratorFirms = createAsyncThunk<
  { caseId: number; firms: NamedFirm[] },
  { caseId: number; firms: NamedFirm[] }
>("case/setCollaboratorFirms", ({ caseId, firms }) =>
  runGqlMutation(SetCollaboratorFirmsDocument, { caseId, firmIds: firms.map((f) => f.id) }).then(() => ({
    caseId,
    firms,
  }))
);

export const setPlaintiffFirm = createAsyncThunk<
  NamedFirm | undefined,
  { caseId: number; plaintiffFirm: NamedFirm | undefined }
>("case/setPlaintiffFirm", ({ caseId, plaintiffFirm }, thunkAPI) =>
  runGqlMutation(SetPlaintiffFirmDocument, { caseId, plaintiffFirmId: plaintiffFirm?.id })
    .then(() => plaintiffFirm)
    .catch((e) => thunkAPI.rejectWithValue(extractGQLError(e)))
);

export const renameIntake = createAsyncThunk<
  { intakeId: number; newName: string },
  { intakeId: number; newName: string }
>("case/renameIntake", ({ intakeId, newName }) =>
  runGqlMutation(RenameDocumentIntakeDocument, { intakeId, newName }).then(() => ({ intakeId, newName }))
);

export const updateIntakeCustomProperties = createAsyncThunk<
  { intakeId: number; properties: KeyValueOptionalInput[] },
  { intakeId: number; properties: KeyValueOptionalInput[] }
>("case/updateIntakeCustomProperties", (props) =>
  runGqlMutation(UpdateIntakeCustomPropertiesDocument, props).then(() => props)
);

export const reintakeDocument = createAsyncThunk<
  { reprocessType: ReprocessType; priority: boolean; intakeId: number; rebuildPDF: boolean },
  { reprocessType: ReprocessType; priority: boolean; intakeId: number; rebuildPDF: boolean }
>("case/reintake", ({ reprocessType, priority, intakeId, rebuildPDF }) =>
  runGqlMutation(ReprocessIntakeDocument, { reprocessType, priority, intakeId, rebuildPDF }).then(() => ({
    reprocessType,
    priority,
    intakeId,
    rebuildPDF,
  }))
);

// Exports Actions
export const fetchCaseExports = createAsyncThunk<GetCaseExportsQuery, { caseId: number }>(
  "case/fetchCaseExports",
  ({ caseId }) => runGqlQuery(GetCaseExportsDocument, { caseId })
);

interface TriggerCaseFormExportRequest {
  caseId: number;
  configId?: number;
  password?: string;
  collectShared: boolean;
  pushToTransfer: boolean;
  customTransferFolder?: string;
}

export const createCaseExport = createAsyncThunk<CaseExport, TriggerCaseFormExportRequest>(
  "case/profileCreateExport",

  (req, thunkAPI) =>
    backend
      .post<TriggerCaseFormExportRequest, CaseExport>("/case/triggerCaseFormExport", {
        caseId: req.caseId,
        pushToTransfer: req.pushToTransfer,
        ...(req.customTransferFolder ? { customTransferFolder: req.customTransferFolder } : {}),
        ...(req.password ? { password: req.password } : {}),
        ...(req.configId ? { configId: req.configId } : { config: {} }),
        collectShared: req.collectShared,
      })
      .catch((e) => thunkAPI.rejectWithValue(e))
);

export const bulkCaseExport = createAsyncThunk<
  BulkCaseProfileExportMutationVariables,
  BulkCaseProfileExportMutationVariables
>("case-table/bulkGenerateExports", (req, thunkAPI) =>
  runGqlMutation(BulkCaseProfileExportDocument, req)
    .then(() => req)
    .catch((e) => thunkAPI.rejectWithValue(e))
);

interface TriggerCaseFullRecordSetExportRequest {
  caseId: number;
  configId?: number;
  password?: string;
  collectShared: boolean;
  pushToTransfer: boolean;
  customTransferFolder?: string;
}

export const createCaseFullRecordSetExport = createAsyncThunk<CaseExport, TriggerCaseFullRecordSetExportRequest>(
  "case/profileCreateFullRecordSetExport",

  (req, thunkAPI) =>
    backend
      .post<TriggerCaseFullRecordSetExportRequest, CaseExport>("/case/triggerCaseFullRecordSetExport", {
        caseId: req.caseId,
        pushToTransfer: req.pushToTransfer,
        ...(req.customTransferFolder ? { customTransferFolder: req.customTransferFolder } : {}),
        ...(req.password ? { password: req.password } : {}),
        ...(req.configId ? { configId: req.configId } : { config: {} }),
        collectShared: req.collectShared,
      })
      .catch((e) => thunkAPI.rejectWithValue(e))
);

export const deleteCaseExport = createAsyncThunk<{ caseExportId: number }, { caseExportId: number }>(
  "case/deleteExport",
  (req) => backend.deleteCaseExport(req.caseExportId)
);

export const updateCaseExportCollectSharedState = createAsyncThunk<
  { caseExportId: number; share: boolean },
  {
    caseId: number;
    caseExportId: number;
    share: boolean;
  }
>("case/shareExport", ({ caseId, caseExportId, share }) =>
  runGqlMutation(UpdateCaseExportCollectSharedStateDocument, {
    caseId,
    caseExportId,
    share,
  }).then(() => ({ caseExportId, share }))
);

export const createCaseChronologyExport = createAsyncThunk<
  CaseExport,
  { caseId: number; collectShared: boolean; pushToTransfer: boolean; customTransferFolder?: string }
>("case/profileCreateChronologyExport", (req, thunkAPI) =>
  backend
    .post<{ caseId: number }, CaseExport>("/case/triggerCaseChronologyExport", req)
    .catch((e) => thunkAPI.rejectWithValue(e))
);

// Review Actions
export function getCaseFindings(variables: GetCaseFindingsQueryVariables): Promise<GetCaseFindingsQuery> {
  return runGqlQuery(GetCaseFindingsDocument, variables);
}

export const fetchBaseFindings = createAsyncThunk<
  GetCaseFindingsQuery,
  { lensId: number; caseId: number; fieldId?: number; intakeIds: number[] }
>("review/fetchBaseFindings", ({ lensId, caseId, fieldId, intakeIds }) => {
  return getCaseFindings({
    lensId,
    caseId,
    filters: {
      ...emptyLensHitFilters,
      // We set the 3 exclude arrays to the empty array, not undefined
      // because we want the base findings to show the full counts.
      // Otherwise, you've got lower counts on the base findings than on the
      // filtered findings, if you drop some filters, and its very confusing
      notPageTypes: [],
      notClusterTypes: [],
      notPageLabels: [],
      ruleProfileFieldId: fieldId,
      intakeIds: intakeIds,
    },
  });
});

export const fetchFilteredFindings = createAsyncThunk<
  GetCaseFindingsQuery,
  { lensId: number; caseId: number; filters: LensHitsFilterParamsInput; sortBy: "default" | "alpha" }
>("review/fetchFilteredFindings", ({ lensId, caseId, filters, sortBy }) => {
  const sortAlpha = sortBy === "alpha" ? true : false;
  return getCaseFindings({ lensId, caseId, filters, sortAlpha: sortAlpha });
});
